import { type FC, memo } from "react";
import type { RequestOrigins, ShiftRequest } from "../../../utils/models";
import { GlyphButton } from "../../UIToolkit/Clickable/GlyphButton";
import { HoverCardOrSheet, HoverCardOrSheetSurface } from "../../UIToolkit/Popups/HoverCardOrSheet";
import { RequestOriginCard } from "./RequestOriginCard";
import { cardTitle } from "./requestOriginCopy";
import { ORIGIN_KIND_ICON, type RequestOriginViewLevel } from "./requestOriginMeta";
import { useOriginCopyContext } from "./useOriginCopyContext";

type Props = {
	request: ShiftRequest;
	origins: RequestOrigins;
	level: RequestOriginViewLevel;
	/** Where the phone sheet stops: above the gaps view's bottom nav on its screens, the screen's edge (`undefined`) elsewhere. */
	sheetBottomOffset: string | undefined;
};

/**
 * A request's origin at rest: one muted glyph after its title, shaped by the first cause, with a count when there are
 * more. Hovering or focusing it opens "Why this shift was posted"; on a phone a tap opens the same card in a sheet, and
 * the tap never reaches the request card around it.
 */
export const RequestOriginGlyph: FC<Props> = memo(({ request, origins, level, sheetBottomOffset }) => {
	const copy = useOriginCopyContext();
	const [first] = origins.openings;
	const Icon = ORIGIN_KIND_ICON[first.kind];
	const causeCount = origins.openings.length;
	return (
		<HoverCardOrSheet
			dataTestId={`request-origin-card-${request.id}-popup`}
			sheetBottomOffset={sheetBottomOffset}
			content={(surface) => (
				<RequestOriginCard request={request} origins={origins} level={level} width={surface === HoverCardOrSheetSurface.Card ? "card" : "fill"} />
			)}
		>
			{({ isOpen, onClick, ariaProps }) => (
				<GlyphButton
					{...ariaProps}
					icon={<Icon />}
					ariaLabel={copy.t("request.origin.glyph.aria", { summary: cardTitle(origins, copy) })}
					badgeCount={causeCount > 1 ? causeCount : undefined}
					active={isOpen}
					onClick={onClick}
					dataTestId={`request-origin-glyph-${request.id}`}
					data-kind={first.kind}
					data-group={first.group}
				/>
			)}
		</HoverCardOrSheet>
	);
});
